import { ChangeEvent, useCallback } from 'react';

import { useControllableState } from '@mint-lib/hooks';
import { ChevronDown, ChevronUp } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { ButtonGroup } from '../../lib/button-group.jsx';
import { InputGroup, InputGroupInput } from '../../lib/input-group.jsx';
import { type InputProps } from '../../lib/input.jsx';

export type CounterProps = {
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder?: string;
  /**
   * The value of the `Input` element, required for a controlled component.
   */
  value?: number;
  /** The size of the component
   * @default medium
   */
  size?: 'small' | 'medium';
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * @default default
   */
  color?: 'default' | 'warning' | 'error';
  /** The default value. Use when the component is not controlled. */
  defaultValue?: number;
  /**
   * If 'true', the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * If 'true', the label is displayed as required and the input will be required.
   * @default false
   */
  required?: boolean;
  /**
   * The id of the input element. Use this prop to make label and helperText accessible for screen readers.
   */
  id?: string;
  /**
   * Callback fired when the value is changed.
   */
  onChange: (value: number) => void;
  /**
   * The props for the input element.
   */
  inputProps?: InputProps;
  /**
   * If 'true', the input will be read only.
   */
  readOnly?: boolean;
  /**
   * The minimum value of the input.
   */
  min: number;
  /**
   * The maximum value of the input.
   */
  max: number;
  /**
   * The class name of the component.
   */
  className?: string;
  isIntegerOnly?: boolean;
};

function Counter({
  id,
  readOnly,
  placeholder,
  value: controlledValue,
  disabled,
  min,
  max,
  className,
  size = 'medium',
  defaultValue,
  onChange,
  color = 'default',
  required,
  inputProps,
}: Readonly<CounterProps>) {
  const [value, setValue] = useControllableState({
    prop: controlledValue,
    defaultProp: defaultValue,
    onChange: onChange,
  });
  const handleIncrease = useCallback(() => {
    if (value < max && !disabled) {
      setValue(value + 1);
    }
  }, [value, disabled, max, setValue]);

  const handleDecrease = useCallback(() => {
    if (value > min && !disabled) {
      setValue(value - 1);
    }
  }, [value, disabled, min, setValue]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = Number(event.target.value);

      if (inputValue >= min && inputValue <= max) {
        setValue(inputValue);
      }
    },
    [min, max, setValue],
  );
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled || readOnly) return;

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        handleIncrease();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        handleDecrease();
      }
    },
    [disabled, readOnly, handleIncrease, handleDecrease],
  );

  return (
    <InputGroup
      className={cn(
        'box-border w-full disabled:bg-action-disabledBackground data-[state=read-only]:rounded-sm',

        className,
      )}
      color={color}
      size={size}
      id={id}
      data-state={readOnly ? 'read-only' : 'enabled'}
      aria-label="counter-container"
    >
      <InputGroupInput
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        inputMode="numeric"
        pattern="[0-9]*"
        min={min}
        max={max}
        aria-label="counter-input"
        {...inputProps}
      />

      {!readOnly && (
        <ButtonGroup
          data-slot="counter-buttons"
          orientation="vertical"
          className="mr-2"
        >
          <button
            type="button"
            data-size={size}
            data-slot="increase-button"
            aria-label="increase-button"
            onClick={handleIncrease}
            disabled={disabled}
            className="flex cursor-pointer items-center justify-center rounded-tl-xl rounded-tr-xl bg-greyscale-grey10 fill-icon-primary p-0 hover:bg-greyscale-grey20 focus-visible:outline-none disabled:bg-action-disabledBackground disabled:fill-action-disabled02 disabled:text-action-disabled02 data-[size=medium]:size-4 data-[size=small]:size-3.5"
          >
            <ChevronUp className="size-4" />
          </button>
          <button
            type="button"
            data-size={size}
            data-slot="decrease-button"
            aria-label="decrease-button"
            onClick={handleDecrease}
            disabled={disabled}
            className="flex cursor-pointer items-center justify-center rounded-br-xl rounded-bl-xl bg-greyscale-grey10 fill-icon-primary p-0 hover:bg-greyscale-grey20 focus-visible:outline-none disabled:bg-action-disabledBackground disabled:fill-action-disabled02 disabled:text-action-disabled02 data-[size=medium]:size-4 data-[size=small]:size-3.5"
          >
            <ChevronDown className="size-4" />
          </button>
        </ButtonGroup>
      )}
    </InputGroup>
  );
}

export default Counter;
